// Seat selection (Headout SEATMAP activities). Everything here is inert for other suppliers.

export const SEATMAP_BOOKING_TYPE = 'SEATMAP'
export const MAX_SEATMAP_SEATS = 20

// Headout's seat picker iframe sends seat objects; only these fields are kept. `id` (SE-...) is the
// value the supplier accepts — `seatCode` is a display label ("14K") and must never be sent as an id.
export interface SelectedSeat {
  id: string
  section?: string
  row?: string
  number?: string
  label?: string
  price?: number
}

export const isSeatmapActivity = (...sources: any[]): boolean =>
  sources.some((source) => source?.booking_type === SEATMAP_BOOKING_TYPE)

export const normalizeSeat = (raw: any): SelectedSeat | null => {
  const id = raw?.id
  if (!id || typeof id !== 'string') return null
  return {
    id,
    section: raw.seatSection ? String(raw.seatSection) : undefined,
    row: raw.seatRow ? String(raw.seatRow) : undefined,
    number: raw.seatNumber ? String(raw.seatNumber) : undefined,
    label: raw.seatCode ? String(raw.seatCode) : undefined,
    price: typeof raw.price === 'number' ? raw.price : undefined,
  }
}

// Seat details only exist in the browser that picked them (order-api does not return them yet), so
// they are remembered per seat id for the checkout / confirmation / order screens. Best effort.
const STORAGE_PREFIX = 'seat:'

export const rememberSeats = (seats: SelectedSeat[]) => {
  try {
    seats.forEach((seat) => localStorage.setItem(STORAGE_PREFIX + seat.id, JSON.stringify(seat)))
  } catch {
    // storage unavailable: screens fall back to "N seats"
  }
}

const recall = (id: string): SelectedSeat | null => {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + id)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export const describeSeat = (seat: Partial<SelectedSeat> | null | undefined, t: (key: string, params?: any) => string): string => {
  if (!seat) return ''
  const parts: string[] = []
  if (seat.section) parts.push(t('Section {section}', { section: seat.section }))
  if (seat.row) parts.push(t('Row {row}', { row: seat.row }))
  if (seat.number) parts.push(t('Seat {number}', { number: seat.number }))
  if (parts.length) return parts.join(', ')
  return seat.label || ''
}

// Seat info for one order availability: order-api fields if it ever returns them, else what this
// browser remembered. Returns '' when nothing is known.
export const availabilitySeatLabel = (availability: any, t: (key: string, params?: any) => string): string => {
  const fromApi = availability?.seat || availability?.seat_info
  if (fromApi && typeof fromApi === 'object') {
    const described = describeSeat(
      { section: fromApi.section, row: fromApi.row, number: fromApi.number, label: fromApi.label || fromApi.code },
      t
    )
    if (described) return described
  }
  if (typeof availability?.seat_code === 'string' && availability.seat_code) {
    const remembered = recall(availability.seat_code)
    return remembered ? describeSeat(remembered, t) : ''
  }
  const id = availability?.ticket_type_id ?? availability?.ticket_type?.id
  if (typeof id === 'string' && id.startsWith('SE-')) {
    // ETL names each seat ticket ("Front Stalls · Row G · Seat 49") and order-api keeps it as holder_title,
    // so this works on any device; the browser's own memory of the pick is the fallback.
    return describeSeat(recall(id), t) || availability?.holder_title || ''
  }
  return ''
}

export const isSeatmapProduct = (product: any): boolean => {
  if (product?.booking_type === SEATMAP_BOOKING_TYPE) return true
  return (product?.availabilities || []).some((a: any) => {
    const id = a?.ticket_type_id ?? a?.ticket_type?.id ?? a?.seat_code
    return typeof id === 'string' && id.startsWith('SE-')
  })
}
